import { type Icon, IMenuItem, ISubMenuItemRendererProps } from "@comact/crc";
import _ from "lodash";
import { IFeatureFlagTypes } from "../../featureFlags/model";
import { ITemplate } from "../../nodeTemplate/model";
import { IPermissionLevel, Levels, Permissions } from "../../users";
import { INode } from "../model";

export type IPerspectivePosition = "header" | "menu" | "overview" | "sidebar";

export type IPerspective = "details/backups" | "details/backups/application-logs" | "details/backups/service-backups" | "details/backups/system-backups" |
    "schedule" | "cloud-shifts-edit" | "cloud-shifts-edit/definition" | "team-edit" | "team" | "cloud-shifts-edit/shift" | "cmoc-backups-restore" |
    "details" | "details/editLinks" | "details/edit/KpiRegistrations" | "events" | "events/downtime-app" | "events/downtime-app/downtimes/admin/edit" |
    "events/downtime-app/watchlist" | "events/downtime-app/watchlist/edit" | "link" | "my-preferences" | "notifications" | "notifications/grafana-alerts" |
    "notifications/grafana-alerts/edit" | "notifications/notification-groups" | "notifications/notification-groups/edit" | "notifications/scheduled-reports" |
    "notifications/scheduled-reports/edit" | "overview" | "organization-settings" | "organization-settings/external-provider" |
    "organization-settings/grafana-integration" | "organization-settings/third-party-integration" | "reports" |
    "resource-library" | "resource-library/user-manuals" | "resource-library/service-reports" | "resource-library/technical-drawings" | "resource-library/maintenance-manuals" |
    "resource-library/trainings" | "templates" | "user-management" | "user-management/events/groups" |
    "user-management/groups" | "user-management/groups/edit" | "user-management/users" | "user-management/users/edit" |
    "events/downtime-app/settings" | "events/downtime-app/causes/edit" | "inventory/configuration/edit" | "inventory/configuration/editCounts" |
    "inventory/configuration" | "inventory/diff" | "inventory/history" | "custom-forms" | "custom-forms-edit" | "inventory/configuration/editBfPerPack" | "odometers" |
    "inventory/rough-inventory" | "events/downtime-app/downtimes/admin" | "events/downtime-app/downtimes/operator" | "customer/requests" | "customer/requests/create" |
    "pmPackForecasting/configuration" | "pmPackForecasting/projectionRun" | "pmPackForecasting/volumeForecast" | "dashboards" | "components-engine" | "events/timeline";

export interface IPerspectiveDefinition {
    id: IPerspective;
    parent: IPerspective; // The parent perspective, to create menu hierarchy. Ideally of the type IPerspective but that makes circular references.
    templates: ITemplate["name"][]; // Templates that have access to this perspective. Null indicates that the perspective is available from any places.
    systemPerspective?: boolean; // Activate this property if the perspective is a system perspective (not a node perspective)
    perspectivePosition: IPerspectivePosition[]; // Where the perspective can be shown
    permissions: IPermissionLevel[]; // List of all needed permissions to use this perspective
    featureFlags: IFeatureFlagTypes[]; // List of the feature flags needed to use this perspective
    conditions?: ("account" | "user")[] | null;
    validPlatform: ("cmoc" | "icp")[]; // List of valid platforms
    priority?: number; // The order of the perspective in the menu. Perspectives without any priority come first and are sorted has they comes in the definition.
    hideInSubmenu?: boolean; // If this perspective must be hide in submenu
    hidden?: () => boolean; // Special case to indicates that this perspective must be hidden
    runWithoutNodes?: boolean; // Does it need to run without any nodes (not iep machines) false by default
    customSubMenuRenderer?: React.NamedExoticComponent<ISubMenuItemRendererProps>;
    exposeWhenZeroNodes?: boolean;
}

// An instance of a perspective (link) for a specific node
export interface IPerspectiveInstance extends IMenuItem {
    id: IPerspective | string;
    perspectiveId: IPerspective;
    perspectiveParentId: IPerspective;
    icon: React.ComponentPropsWithoutRef<typeof Icon>["type"];
    isAlternate: boolean;
}

const allPerspectivesDefinitionsArray: IPerspectiveDefinition[] = [
    {
        id: "overview",
        parent: null,
        templates: ["customer", "location"],
        perspectivePosition: ["header"],
        permissions: [],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
        runWithoutNodes: true,
    },
    {
        id: "my-preferences",
        parent: null,
        templates: ["millCodec"],
        systemPerspective: true,
        perspectivePosition: [],
        permissions: [],
        featureFlags: [],
        conditions: ["user"],
        hideInSubmenu: true,
        validPlatform: ["cmoc", "icp"],
        priority: 10,
    },
    {
        id: "user-management",
        parent: null,
        templates: null,
        systemPerspective: true,
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.UserManagement, level: Levels.View }],
        featureFlags: [],
        hideInSubmenu: true,
        validPlatform: ["cmoc", "icp"],
        priority: 10,
    },
    {
        id: "user-management/users",
        parent: "user-management",
        templates: null,
        systemPerspective: true,
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.UserManagement, level: Levels.View }],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
    },
    {
        id: "user-management/users/edit",
        parent: "user-management/users",
        templates: null,
        systemPerspective: true,
        perspectivePosition: [],
        permissions: [{ permission: Permissions.UserManagement, level: Levels.Edit }],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
    },
    {
        id: "user-management/groups",
        parent: "user-management",
        templates: null,
        systemPerspective: true,
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.UserManagement, level: Levels.View }],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
    },
    {
        id: "user-management/groups/edit",
        parent: "user-management/groups",
        templates: null,
        systemPerspective: true,
        perspectivePosition: [],
        permissions: [{ permission: Permissions.UserManagement, level: Levels.Edit }],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
    },
    {
        id: "user-management/events/groups",
        parent: "user-management/groups",
        templates: [],
        systemPerspective: true,
        perspectivePosition: [],
        permissions: [],
        featureFlags: [],
        validPlatform: [], // Disabled for now (ICP-2208)
    },
    {
        id: "templates",
        parent: null,
        templates: null, // Available from any places
        systemPerspective: true,
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.SystemConfiguration, level: Levels.Admin }],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
    },
    {
        id: "organization-settings",
        parent: null,
        templates: null,
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.ApiToken, level: Levels.View }],
        featureFlags: [],
        validPlatform: ["icp"],
        hideInSubmenu: true,
        exposeWhenZeroNodes: true,
    },
    {
        id: "organization-settings/external-provider",
        parent: "organization-settings",
        templates: null,
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.ExternalAuthProviderConfiguration, level: Levels.View }],
        featureFlags: ["ExternalProvider"],
        validPlatform: ["icp"],
        exposeWhenZeroNodes: true,
        runWithoutNodes: true,
    },
    {
        id: "organization-settings/third-party-integration",
        parent: "organization-settings",
        templates: null,
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.ApiToken, level: Levels.View }],
        featureFlags: ["RawData"],
        validPlatform: ["icp"],
        hideInSubmenu: true,
        exposeWhenZeroNodes: true,
        runWithoutNodes: true,
    },
    {
        id: "organization-settings/grafana-integration",
        parent: "organization-settings",
        templates: null,
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.GrafanaIntegrationConfiguration, level: Levels.Admin }],
        featureFlags: ["GrafanaIntegration"],
        validPlatform: ["icp"],
        exposeWhenZeroNodes: true,
    },
    {
        id: "notifications",
        parent: null,
        templates: ["location"],
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.NotificationManagement, level: Levels.View }],
        featureFlags: ["GrafanaAlarms", "SummarizedReporting"],
        validPlatform: ["icp"],
        hideInSubmenu: true,
    },
    {
        id: "notifications/grafana-alerts",
        parent: "notifications",
        templates: ["location"],
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.NotificationManagement, level: Levels.View }],
        featureFlags: ["GrafanaAlarms"],
        validPlatform: ["icp"],
    },
    {
        id: "notifications/grafana-alerts/edit",
        parent: "notifications/grafana-alerts",
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.NotificationManagement, level: Levels.Edit }],
        featureFlags: ["GrafanaAlarms"],
        validPlatform: ["icp"],
    },
    {
        id: "notifications/notification-groups",
        parent: "notifications",
        templates: ["location"],
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.NotificationManagement, level: Levels.View }],
        featureFlags: ["GrafanaAlarms"],
        validPlatform: ["icp"],
    },
    {
        id: "notifications/notification-groups/edit",
        parent: "notifications/notification-groups",
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.NotificationManagement, level: Levels.Edit }],
        featureFlags: ["GrafanaAlarms"],
        validPlatform: ["icp"],
    },
    {
        id: "notifications/scheduled-reports",
        parent: "notifications",
        templates: ["location"],
        perspectivePosition: ["menu"],
        permissions: [{ permission: Permissions.NotificationManagement, level: Levels.View }],
        featureFlags: ["SummarizedReporting"],
        validPlatform: ["icp"],
    },
    {
        id: "notifications/scheduled-reports/edit",
        parent: "notifications/scheduled-reports",
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.NotificationManagement, level: Levels.Edit }],
        featureFlags: ["SummarizedReporting"],
        validPlatform: ["icp"],
    },
    {
        id: "events",
        parent: null,
        templates: ["location"],
        systemPerspective: false,
        perspectivePosition: ["header"],
        permissions: [],
        featureFlags: [],
        hideInSubmenu: true,
        validPlatform: ["cmoc"],
    },
    {
        id: "details/backups",
        parent: "details",
        templates: ["machineCodec", "machineComponent"],
        hideInSubmenu: true,
        perspectivePosition: [],
        permissions: [{ permission: Permissions.CloudBackups, level: Levels.View }],
        featureFlags: [],
        validPlatform: ["icp", "cmoc"],
    },
    {
        id: "details/backups/system-backups",
        parent: "details/backups",
        templates: ["machineCodec"],
        perspectivePosition: ["sidebar"],
        permissions: [{ permission: Permissions.CloudBackups, level: Levels.View }],
        featureFlags: [],
        validPlatform: ["icp", "cmoc"],
    },
    {
        id: "details/backups/application-logs",
        parent: "details/backups",
        templates: ["machineComponent"],
        perspectivePosition: ["sidebar"],
        permissions: [{ permission: Permissions.CloudBackups, level: Levels.View }],
        featureFlags: [],
        validPlatform: ["icp", "cmoc"],
    },
    {
        id: "details/backups/service-backups",
        parent: "details/backups",
        templates: ["machineComponent"],
        perspectivePosition: ["sidebar"],
        permissions: [{ permission: Permissions.CloudBackups, level: Levels.View }],
        featureFlags: [],
        validPlatform: ["icp", "cmoc"],
    },
    {
        id: "details",
        parent: null,
        templates: null,
        perspectivePosition: ["header", "sidebar"],
        permissions: [{ permission: Permissions.SystemConfiguration, level: Levels.Admin }],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
    },
    {
        id: "details/editLinks",
        parent: "details",
        templates: null,
        perspectivePosition: [],
        permissions: [{ permission: Permissions.SystemConfiguration, level: Levels.Admin }],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
    },
    {
        id: "details/edit/KpiRegistrations",
        parent: "details",
        templates: ["millCodec"],
        perspectivePosition: ["sidebar"],
        permissions: [{ permission: Permissions.SystemConfiguration, level: Levels.Admin }],
        featureFlags: [],
        validPlatform: ["icp"],
    },
    {
        id: "cmoc-backups-restore", // iframe icp to download a cloud backup into cmoc
        parent: null,
        templates: ["machineCodec"],
        perspectivePosition: [],
        permissions: [],
        featureFlags: [],
        validPlatform: ["icp"],
    },
    {
        id: "link",
        parent: null,
        templates: null,
        perspectivePosition: [],
        permissions: [],
        featureFlags: [],
        validPlatform: ["cmoc", "icp"],
    },
    {
        id: "events/downtime-app",
        parent: "events",
        templates: ["location"],
        perspectivePosition: ["header"],
        permissions: [],
        featureFlags: ["DowntimeAdmin"],
        validPlatform: ["icp"],
    },
    {
        id: "events/downtime-app/downtimes/admin/edit",
        parent: "events/downtime-app/downtimes/admin",
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.Downtimes, level: Levels.Admin }],
        featureFlags: ["DowntimeAdmin"],
        validPlatform: ["icp"], // O8-47 was for a demo, this is disable since it's only working with mocks
    },
    {
        id: "events/downtime-app/downtimes/admin",
        parent: "events/downtime-app",
        templates: ["location"],
        perspectivePosition: ["overview", "header"],
        permissions: [{ permission: Permissions.Downtimes, level: Levels.Admin }],
        featureFlags: ["DowntimeAdmin"],
        validPlatform: ["icp"], // O8-47 was for a demo, this is disable since it's only working with mocks
    },
    {
        id: "events/downtime-app/downtimes/operator",
        parent: "events/downtime-app",
        templates: ["location"],
        perspectivePosition: ["overview", "header"],
        permissions: [{ permission: Permissions.Downtimes, level: Levels.View }],
        featureFlags: ["DowntimeOperator"],
        validPlatform: ["icp"],
    },
    {
        id: "events/downtime-app/settings",
        parent: "events/downtime-app/downtimes/admin",
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.Downtimes, level: Levels.Admin }],
        featureFlags: ["DowntimeAdmin"],
        validPlatform: ["icp"],
    },
    {
        id: "events/downtime-app/causes/edit",
        parent: "events/downtime-app/settings",
        templates: ["machineCodec", "millCodec", "location", "plcComponent", "genericDevice"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.Downtimes, level: Levels.Admin }],
        featureFlags: ["DowntimeAdmin"],
        validPlatform: ["icp"],
    },
    {
        id: "reports",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.Report, level: Levels.View }],
        hideInSubmenu: true,
        featureFlags: ["FlaskReporting"],
        validPlatform: ["icp"],
    },
    {
        id: "resource-library",
        parent: null,
        templates: ["location"],
        perspectivePosition: ["header"],
        permissions: [{ permission: Permissions.ResourceLibrary, level: Levels.View }],
        hideInSubmenu: true,
        featureFlags: ["ResourceLibrary"],
        validPlatform: ["icp"],
    },
    {
        id: "resource-library/user-manuals",
        parent: "resource-library",
        templates: ["location"],
        perspectivePosition: ["header"],
        permissions: [{ permission: Permissions.ResourceLibrary, level: Levels.View }],
        featureFlags: ["ResourceLibrary"],
        validPlatform: ["icp"],
    },
    {
        id: "resource-library/service-reports",
        parent: "resource-library",
        templates: ["location"],
        perspectivePosition: ["header"],
        permissions: [{ permission: Permissions.ResourceLibrary, level: Levels.View }],
        featureFlags: ["ResourceLibrary"],
        validPlatform: ["icp"],
    },
    {
        id: "resource-library/technical-drawings",
        parent: "resource-library",
        templates: ["location"],
        perspectivePosition: ["header"],
        permissions: [{ permission: Permissions.ResourceLibrary, level: Levels.View }],
        featureFlags: ["ResourceLibrary"],
        validPlatform: ["icp"],
    },
    {
        id: "resource-library/maintenance-manuals",
        parent: "resource-library",
        templates: ["location"],
        perspectivePosition: ["header"],
        permissions: [{ permission: Permissions.ResourceLibrary, level: Levels.View }],
        featureFlags: ["ResourceLibrary"],
        validPlatform: ["icp"],
    },
    {
        id: "resource-library/trainings",
        parent: "resource-library",
        templates: ["location"],
        perspectivePosition: ["header"],
        permissions: [{ permission: Permissions.ResourceLibrary, level: Levels.View }],
        featureFlags: ["ResourceLibrary"],
        validPlatform: ["icp"],
    },
    {
        id: "inventory/configuration",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.RoughInventoryTracking, level: Levels.View }],
        featureFlags: ["RoughInventory"],
        validPlatform: ["icp"],
    },
    {
        id: "inventory/configuration/edit",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.RoughInventoryTracking, level: Levels.Admin }],
        featureFlags: ["RoughInventory"],
        validPlatform: ["icp"],
    },
    {
        id: "inventory/configuration/editCounts",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.RoughInventoryTracking, level: Levels.Edit }],
        featureFlags: ["RoughInventory"],
        validPlatform: ["icp"],
    },
    {
        id: "inventory/diff",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.RoughInventoryTracking, level: Levels.View }],
        featureFlags: ["RoughInventory"],
        validPlatform: ["icp"],
    },
    {
        id: "inventory/history",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.RoughInventoryTracking, level: Levels.View }],
        featureFlags: ["RoughInventory"],
        validPlatform: ["icp"],
    },
    {
        id: "custom-forms",
        parent: null,
        templates: ["location", "millCodec"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.CustomForms, level: Levels.View }],
        featureFlags: ["CustomForm"],
        validPlatform: ["icp"],
    },
    {
        id: "custom-forms-edit",
        parent: null,
        templates: ["location", "millCodec"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.CustomForms, level: Levels.Admin }],
        featureFlags: ["CustomForm"],
        validPlatform: ["icp"],
    },
    {
        id: "schedule",
        parent: null,
        templates: ["location"],
        perspectivePosition: ["overview"],
        permissions: [{ permission: Permissions.ScheduleWorkTeamConfiguration, level: Levels.View }],
        featureFlags: ["CloudShifts"],
        validPlatform: ["icp"],
    },
    {
        id: "cloud-shifts-edit",
        parent: null,
        templates: ["customer", "location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.ScheduleWorkTeamConfiguration, level: Levels.Edit }],
        featureFlags: ["CloudShifts"],
        validPlatform: ["icp"],
    },
    {
        id: "cloud-shifts-edit/shift",
        parent: null,
        templates: ["customer", "location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.ScheduleWorkTeamConfiguration, level: Levels.Edit }],
        featureFlags: ["CloudShifts"],
        validPlatform: ["icp"],
    },
    {
        id: "cloud-shifts-edit/definition",
        parent: null,
        templates: ["customer", "location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.ScheduleWorkTeamConfiguration, level: Levels.Edit }],
        featureFlags: ["CloudShifts"],
        validPlatform: ["icp"],
    },
    {
        id: "team",
        parent: null,
        templates: ["location", "millCodec"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.ScheduleWorkTeamConfiguration, level: Levels.View }],
        featureFlags: ["ShiftTeams"],
        validPlatform: ["icp"],
    },
    {
        id: "team-edit",
        parent: "team",
        templates: ["location", "millCodec"],
        perspectivePosition: null,
        permissions: [{ permission: Permissions.ScheduleWorkTeamConfiguration, level: Levels.View }],
        featureFlags: ["ShiftTeams"],
        validPlatform: ["icp"],
    },
    {
        id: "customer/requests",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.ServiceRequest, level: Levels.View }],
        featureFlags: ["Salesforce"],
        validPlatform: ["icp"],
    },
    {
        id: "customer/requests/create",
        parent: "customer/requests",
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.ServiceRequest, level: Levels.Edit }],
        featureFlags: ["Salesforce"],
        validPlatform: ["icp"],
    },
    {
        id: "odometers",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.Odometers, level: Levels.View }],
        featureFlags: ["Odometer"],
        validPlatform: ["icp"],
    },
    {
        id: "pmPackForecasting/configuration",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.PmPackForecasting, level: Levels.Edit }],
        featureFlags: ["PlanermillPackForecasting"],
        validPlatform: ["icp"],
    },
    {
        id: "pmPackForecasting/projectionRun",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.PmPackForecasting, level: Levels.View }],
        featureFlags: ["PlanermillPackForecasting"],
        validPlatform: ["icp"],
    },
    {
        id: "pmPackForecasting/volumeForecast",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.PmPackForecasting, level: Levels.View }],
        featureFlags: ["PlanermillPackForecasting"],
        validPlatform: ["icp"],
    },
    {
        id: "dashboards",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.GrafanaDashboard, level: Levels.View }],
        featureFlags: ["GrafanaDashboard"],
        validPlatform: ["icp"],
    },
    {
        id: "components-engine",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.ComponentsEngine, level: Levels.View }],
        featureFlags: ["ComponentsEngine"],
        validPlatform: ["icp"],
    },
    {
        id: "events/timeline",
        parent: null,
        templates: ["location"],
        perspectivePosition: [],
        permissions: [{ permission: Permissions.SmartVision, level: Levels.View }],
        featureFlags: ["SmartVision"],
        validPlatform: ["icp"],
    },
];

export const allPerspectivesDefinitions = _.keyBy(allPerspectivesDefinitionsArray, ({ id }) => id) as Record<IPerspective, IPerspectiveDefinition>;

/**
 * Return the asked perpective for a node if it's available, otherwise, return an available fallback.
 */
export const getNodePerspectiveOrFallback = (nodeTemplate: INode["templateName"], askPerspective: IPerspective): IPerspective => {
    if (askPerspective == allPerspectivesDefinitions.details.id) return askPerspective; // The "details" perspective is always available, no need for more logic

    if (allPerspectivesDefinitions[askPerspective].templates?.includes(nodeTemplate)) return askPerspective; // The requested perpective is available for this node template
    else if (allPerspectivesDefinitions.overview.templates?.includes(nodeTemplate)) return allPerspectivesDefinitions.overview.id; // Check if we can fallback on the overview perspective
    return allPerspectivesDefinitions.details.id; // The ultimate fallback perspective => "details", wich it good for every node
};
